import { Col, Row, Skeleton } from "antd";
import { useCallback, useEffect, useState } from "react";
import { injectIntl, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import Page from "../../Common/Page";
import PermissionCheck from "../../Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHttp } from "../../hooks/useHttp";
import EditButton from "./EditButton";
import StandingsAdjustmentsForm from "./StandingsAdjustmentsForm";

function EditStandingsAdjustments(props) {
  const { formatMessage } = useIntl();

  const { sport, organizationId, seasonId, standingsAdjustmentId } = useParams();
  const {
    loading: fetchStandingsAdjustmentsLoading,
    error: fetchStandingsAdjustmentsError,
    request: fetchStandingsAdjustmentsRequest,
    cleanError: fetchStandingsAdjustmentsCleanError,
  } = useHttp();
  const {
    loading: fetchEntitiesLoading,
    error: fetchEntitiesError,
    request: fetchEntitiesRequest,
    cleanError: fetchEntitiesCleanError,
  } = useHttp();
  const {
    loading: fetchPoolsLoading,
    error: fetchPoolsError,
    request: fetchPoolsRequest,
    cleanError: fetchPoolsCleanError,
  } = useHttp();
  const [standingsAdjustments, setStandingsAdjustments] = useState(null);
  const [formStandingsAdjustments, setFormStandingsAdjustments] = useState(null);
  const [entityName, setEntityName] = useState(" ");
  const [entityList, setEntityList] = useState(null);
  const [poolList, setPoolList] = useState([]);

  const titleBase =
    `${entityName} - ` +
    formatMessage({
      id: "standings.adjustments",
      defaultValue: "Standings Adjustments",
    });

  useEffect(() => {
    const fetchStandingsAdjustments = async () => {
      try {
        const res = await fetchStandingsAdjustmentsRequest({
          url: `/v1/${sport}/o/${organizationId}/standings/adjustments/${standingsAdjustmentId}`,
        });
        if (res?.data?.length) {
          const processedData = {
            ...res.data[0],
          };

          setStandingsAdjustments(processedData);
        }
      } catch (e) {
        console.error(e);
      }
    };

    fetchStandingsAdjustments();
  }, []);

  const fetchEntities = useCallback(async () => {
    try {
      const res = await fetchEntitiesRequest({
        url: `/v1/${sport}/o/${organizationId}/seasons/${seasonId}/entities?include=organization,entities,seasons&limit=1000`,
      });

      let combineResourceData = [];
      if (res?.data && res?.includes?.resources?.entities) {
        combineResourceData = res?.data || [];
        combineResourceData.forEach((result, index) => {
          combineResourceData[index] = {
            ...result,
            ...res.includes.resources.entities[result.entity.id],
          };
        });
      }

      if (combineResourceData?.length && standingsAdjustments?.length) {
        const entityName = combineResourceData[standingsAdjustments.entityId].nameFullLocal || "";
        setEntityName(entityName);
      }

      setEntityList(combineResourceData);
    } catch (e) {
      console.error(e);
    }
  }, [fetchEntitiesRequest, sport, organizationId, seasonId]);

  const fetchPools = useCallback(async () => {
    try {
      const res = await fetchPoolsRequest({
        url: `/v1/${sport}/o/${organizationId}/seasons/${seasonId}/pools`,
      });

      setPoolList(res?.data?.sort((a, b) => a.sortOrder - b.sortOrder) || []);
    } catch (e) {
      console.error(e);
    }
  }, [fetchPoolsRequest, sport, organizationId, seasonId]);

  useEffect(() => {
    fetchEntities();
    fetchPools();
  }, [fetchEntities, fetchPools, standingsAdjustmentId]);

  const handleSetFormRef = useCallback((form) => {
    setFormStandingsAdjustments(form);
  }, []);

  const handleGetFormData = useCallback(async () => {
    const standingsAdjustmentsFormDataSource = await formStandingsAdjustments.validateFields();
    return {
      ...standingsAdjustmentsFormDataSource,
    };
  }, [formStandingsAdjustments]);

  if (fetchStandingsAdjustmentsLoading || !standingsAdjustments) return <Skeleton />;

  return (
    <Page title={titleBase} tabTitle={titleBase} history={props?.history}>
      <Row>
        <Col xs={{ span: 24, offset: 0 }} lg={{ span: 14, offset: 6 }}>
          {standingsAdjustments && (
            <StandingsAdjustmentsForm
              standingsAdjustments={standingsAdjustments}
              handleSetFormRef={handleSetFormRef}
              entityList={entityList}
              poolList={poolList}
            />
          )}
          {standingsAdjustments && (
            <PermissionCheck
              permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}
              permissionOptions={[PermissionOperations.UPDATE]}
            >
              <EditButton handleGetFormData={handleGetFormData} />
            </PermissionCheck>
          )}
        </Col>
      </Row>
    </Page>
  );
}

export default EditStandingsAdjustments;
